import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button, Card, Alert, Col, Row, Modal } from 'react-bootstrap';
import { Map, Marker } from 'react-map-gl';
import axiosInstance from '../api/axiosInstance';
import config from '../config';
import './AddLocation.css';

const ALERT_CATEGORY_TO_FACILITY_TYPE = {
  poison_bait: { en: 'Poison Bait Alert', tc: '毒餌通報' },
  glass: { en: 'Broken Glass Alert', tc: '玻璃碎片通報' },
  lost_pet: { en: 'Lost Pet Alert', tc: '走失寵物通報' },
  dog_walk_buddy: { en: 'Dog Walk Buddy', tc: '狗友散步配對' },
};

const FACILITY_CATEGORY_TO_FACILITY_TYPE = {
  pet_garden: { en: 'PET GARDEN', tc: '寵物公園' },
  pet_friendly_restaurant: { en: 'Pet Friendly Restaurant', tc: '寵物友善餐廳' },
  pet_shop: { en: 'Pet Shop', tc: '寵物用品店' },
  veterinary_clinic: { en: 'Veterinary Clinic', tc: '獸醫診所' },
  pet_grooming: { en: 'Pet Grooming', tc: '寵物美容' },
  pet_boarding: { en: 'Pet Boarding', tc: '寵物寄養' },
  dog_training_center: { en: 'Dog Training Center', tc: '狗隻訓練中心' },
  pet_cafe: { en: 'Pet Cafe', tc: '寵物咖啡店' },
};

const HONG_KONG_CENTER = { longitude: 114.1694, latitude: 22.3193, zoom: 12 };
const MAX_PHOTO_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_PHOTO_MIME_TYPES = ['image/jpeg'];

const AddLocationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [categoryType, setCategoryType] = useState('alert');
  const [alertCategory, setAlertCategory] = useState('poison_bait');
  const [facilityCategory, setFacilityCategory] = useState('pet_garden');
  const [nameEn, setNameEn] = useState('');
  const [nameTc, setNameTc] = useState('');
  const [addressEn, setAddressEn] = useState('');
  const [addressTc, setAddressTc] = useState('');
  const [districtEn, setDistrictEn] = useState('');
  const [districtTc, setDistrictTc] = useState('');
  const [facilityTypeEn, setFacilityTypeEn] = useState('');
  const [facilityTypeTc, setFacilityTypeTc] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [alertDetailsEn, setAlertDetailsEn] = useState('');
  const [alertDetailsTc, setAlertDetailsTc] = useState('');
  const [photos, setPhotos] = useState([]);
  const [coverPhoto, setCoverPhoto] = useState(null);
  const [isMapPickerOpen, setIsMapPickerOpen] = useState(false);
  const [mapPickerView, setMapPickerView] = useState(HONG_KONG_CENTER);
  const [pickedCoords, setPickedCoords] = useState(null);
  const [userCurrentLocation, setUserCurrentLocation] = useState(null);

  useEffect(() => {
    if (!navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserCurrentLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      () => {
        // Keep silent and fallback to default center when location is unavailable.
      }
    );
  }, []);

  const getCurrentPositionAsync = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 60000,
      });
    });

  const openMapPicker = async () => {
    let initialCenter = HONG_KONG_CENTER;

    if (
      Number.isFinite(userCurrentLocation?.latitude) &&
      Number.isFinite(userCurrentLocation?.longitude)
    ) {
      initialCenter = {
        longitude: userCurrentLocation.longitude,
        latitude: userCurrentLocation.latitude,
        zoom: 15,
      };
    } else {
      try {
        const position = await getCurrentPositionAsync();
        const currentLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        setUserCurrentLocation(currentLocation);
        initialCenter = {
          longitude: currentLocation.longitude,
          latitude: currentLocation.latitude,
          zoom: 15,
        };
      } catch (geoError) {
        // Keep silent and fallback to Hong Kong center when location is unavailable.
      }
    }

    setMapPickerView(initialCenter);
    setPickedCoords({
      latitude: initialCenter.latitude,
      longitude: initialCenter.longitude,
    });
    setIsMapPickerOpen(true);
  };

  const confirmMapPick = () => {
    if (!pickedCoords) {
      setError('請先在地圖上點選位置');
      return;
    }
    setLatitude(pickedCoords.latitude.toFixed(6));
    setLongitude(pickedCoords.longitude.toFixed(6));
    setIsMapPickerOpen(false);
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLatitude(position.coords.latitude.toString());
          setLongitude(position.coords.longitude.toString());
        },
        (error) => {
          setError('Failed to get current location');
          console.error('Geolocation error:', error);
        }
      );
    } else {
      setError('Geolocation is not supported');
    }
  };

  const validateSelectedPhotos = (selectedFiles) => {
    for (const file of selectedFiles) {
      if (!ALLOWED_PHOTO_MIME_TYPES.includes(file.type)) {
        return '只允許上傳 JPG/JPEG 格式相片';
      }

      if (file.size > MAX_PHOTO_SIZE_BYTES) {
        return `相片 ${file.name} 超過 5MB，請壓縮後再上傳`;
      }
    }

    return '';
  };

  const validateSinglePhoto = (file) => {
    if (!file) return '';

    if (!ALLOWED_PHOTO_MIME_TYPES.includes(file.type)) {
      return '只允許上傳 JPG/JPEG 格式相片';
    }

    if (file.size > MAX_PHOTO_SIZE_BYTES) {
      return `相片 ${file.name} 超過 5MB，請壓縮後再上傳`;
    }

    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validation
    if (!nameEn && !nameTc) {
      setError('Please provide location name in at least one language');
      setLoading(false);
      return;
    }

    if (!latitude || !longitude) {
      setError('Please provide coordinates or use current location');
      setLoading(false);
      return;
    }

    if (categoryType === 'alert' && !alertDetailsEn && !alertDetailsTc) {
      setError('Please provide alert details in at least one language');
      setLoading(false);
      return;
    }

    if (categoryType === 'facility' && !facilityCategory) {
      setError('請選擇設施類別');
      setLoading(false);
      return;
    }

    const photoValidationMessage = validateSelectedPhotos(photos);
    if (photoValidationMessage) {
      setError(photoValidationMessage);
      setLoading(false);
      return;
    }

    const coverPhotoValidationMessage = validateSinglePhoto(coverPhoto);
    if (coverPhotoValidationMessage) {
      setError(coverPhotoValidationMessage);
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('登入已過期，請重新登入後再試');
        setLoading(false);
        return;
      }

      const mappedAlertFacilityType =
        categoryType === 'alert' ? ALERT_CATEGORY_TO_FACILITY_TYPE[alertCategory] : null;
      const mappedFacilityType =
        categoryType === 'facility' ? FACILITY_CATEGORY_TO_FACILITY_TYPE[facilityCategory] : null;

      const formData = new FormData();
      formData.append('nameEn', nameEn);
      formData.append('nameTc', nameTc);
      formData.append('addressEn', addressEn);
      formData.append('addressTc', addressTc);
      formData.append('districtEn', districtEn);
      formData.append('districtTc', districtTc);
      formData.append(
        'facilityTypeEn',
        categoryType === 'facility' ? (mappedFacilityType?.en || '') : (mappedAlertFacilityType?.en || '')
      );
      formData.append(
        'facilityTypeTc',
        categoryType === 'facility' ? (mappedFacilityType?.tc || '') : (mappedAlertFacilityType?.tc || '')
      );
      formData.append('latitude', String(parseFloat(latitude)));
      formData.append('longitude', String(parseFloat(longitude)));
      formData.append('categoryType', categoryType);

      if (categoryType === 'facility') {
        formData.append('facilityCategory', facilityCategory);
      }

      if (categoryType === 'alert') {
        formData.append('alertCategory', alertCategory);
        formData.append('alertDetailsEn', alertDetailsEn);
        formData.append('alertDetailsTc', alertDetailsTc);
      }

      photos.forEach((photo) => {
        formData.append('photos', photo);
      });

      if (coverPhoto) {
        formData.append('coverPhoto', coverPhoto);
      }

      const response = await axiosInstance.post('/locations/create-user-location', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setSuccess('Location created successfully!');
      setTimeout(() => {
        const detailId = response.data?.data?.documentId || response.data?.data?.id;
        if (detailId) {
          navigate(`/location/${detailId}`);
        } else {
          navigate('/map');
        }
      }, 1500);
    } catch (err) {
      const message =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        `Failed to create location (HTTP ${err.response?.status || 'unknown'})`;
      setError(message);
      console.error('Error creating location:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="add-location-container py-5">
      <h1 className="mb-4">新增地點</h1>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            {/* Location Type */}
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">地點類型</Form.Label>
              <Form.Select
                value={categoryType}
                onChange={(e) => setCategoryType(e.target.value)}
              >
                <option value="facility">設施（狗公園、診所等）</option>
                <option value="alert">通報（提醒）</option>
              </Form.Select>
            </Form.Group>

            {/* Alert Category */}
            {categoryType === 'alert' && (
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">通報類別</Form.Label>
                <Form.Select
                  value={alertCategory}
                  onChange={(e) => setAlertCategory(e.target.value)}
                >
                  <option value="poison_bait">毒餌通報</option>
                  <option value="glass">玻璃碎片</option>
                  <option value="lost_pet">走失寵物</option>
                  <option value="dog_walk_buddy">狗友散步配對</option>
                </Form.Select>
              </Form.Group>
            )}

            {/* Facility Category */}
            {categoryType === 'facility' && (
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">設施類別</Form.Label>
                <Form.Select
                  value={facilityCategory}
                  onChange={(e) => setFacilityCategory(e.target.value)}
                >
                  <option value="pet_garden">寵物公園 (PET GARDEN)</option>
                  <option value="pet_friendly_restaurant">寵物友善餐廳 (Pet Friendly Restaurant)</option>
                  <option value="pet_shop">寵物用品店 (Pet Shop)</option>
                  <option value="veterinary_clinic">獸醫診所 (Veterinary Clinic)</option>
                  <option value="pet_grooming">寵物美容 (Pet Grooming)</option>
                  <option value="pet_boarding">寵物寄養 (Pet Boarding)</option>
                  <option value="dog_training_center">狗隻訓練中心 (Dog Training Center)</option>
                  <option value="pet_cafe">寵物咖啡店 (Pet Cafe)</option>
                </Form.Select>
              </Form.Group>
            )}

            {/* Names */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>名稱（英文）</Form.Label>
                  <Form.Control
                    type="text"
                    value={nameEn}
                    onChange={(e) => setNameEn(e.target.value)}
                    placeholder={categoryType === 'alert' ? 'e.g., Poison bait spotted' : 'e.g., Happy Dog Park'}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>名稱（中文）*</Form.Label>
                  <Form.Control
                    type="text"
                    value={nameTc}
                    onChange={(e) => setNameTc(e.target.value)}
                    placeholder={categoryType === 'alert' ? '例：發現毒餌' : '例：開心狗公園'}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Addresses & Districts (facility only) */}
            {categoryType === 'facility' && (
              <>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>地址（英文）</Form.Label>
                      <Form.Control
                        type="text"
                        value={addressEn}
                        onChange={(e) => setAddressEn(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>地址（中文）</Form.Label>
                      <Form.Control
                        type="text"
                        value={addressTc}
                        onChange={(e) => setAddressTc(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>地區（英文）</Form.Label>
                      <Form.Control
                        type="text"
                        value={districtEn}
                        onChange={(e) => setDistrictEn(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>地區（中文）</Form.Label>
                      <Form.Control
                        type="text"
                        value={districtTc}
                        onChange={(e) => setDistrictTc(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}

            {/* Coordinates */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">座標 *</Form.Label>
              <div className="coordinate-input">
                <Row>
                  <Col md={5}>
                    <Form.Group>
                      <Form.Label>緯度</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.000001"
                        value={latitude}
                        onChange={(e) => setLatitude(e.target.value)}
                        placeholder="e.g., 22.3193"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={5}>
                    <Form.Group>
                      <Form.Label>經度</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.000001"
                        value={longitude}
                        onChange={(e) => setLongitude(e.target.value)}
                        placeholder="e.g., 114.1694"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2} className="d-flex align-items-end">
                    <div className="w-100 d-grid gap-2">
                      <Button
                        variant="outline-primary"
                        onClick={getCurrentLocation}
                        className="w-100"
                      >
                        📍 目前位置
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={openMapPicker}
                        className="w-100"
                      >
                        🗺️ 在地圖內選擇
                      </Button>
                    </div>
                  </Col>
                </Row>
              </div>
            </Form.Group>

            {/* Alert Details */}
            {categoryType === 'alert' && (
              <>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>詳細信息（英文）</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={alertDetailsEn}
                        onChange={(e) => setAlertDetailsEn(e.target.value)}
                        placeholder="Describe the alert..."
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>詳細信息（中文）*</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={alertDetailsTc}
                        onChange={(e) => setAlertDetailsTc(e.target.value)}
                        placeholder="描述通報內容..."
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}

            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">相片（可選，最多 5 張）</Form.Label>
              <Form.Control
                type="file"
                accept=".jpg,.jpeg,image/jpeg"
                multiple
                onChange={(e) => {
                  setError('');
                  const selectedFiles = Array.from(e.target.files || []);

                  if (selectedFiles.length > 5) {
                    setError('最多只能上傳 5 張相片');
                    setPhotos([]);
                    return;
                  }

                  const validationMessage = validateSelectedPhotos(selectedFiles);
                  if (validationMessage) {
                    setError(validationMessage);
                    setPhotos([]);
                    return;
                  }

                  setPhotos(selectedFiles);
                }}
              />
              <Form.Text className="text-muted">只支援 JPG/JPEG，每張不超過 5MB。系統會自動上傳並綁定到此地點。</Form.Text>
              {photos.length > 0 && (
                <div className="selected-photos mt-2">已選擇 {photos.length} 張相片</div>
              )}
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">封面相片（可選，1 張）</Form.Label>
              <Form.Control
                type="file"
                accept=".jpg,.jpeg,image/jpeg"
                onChange={(e) => {
                  setError('');
                  const selectedFile = e.target.files?.[0] || null;

                  if (selectedFile) {
                    const validationMessage = validateSinglePhoto(selectedFile);
                    if (validationMessage) {
                      setError(validationMessage);
                      setCoverPhoto(null);
                      return;
                    }
                  }

                  setCoverPhoto(selectedFile);
                }}
              />
              <Form.Text className="text-muted">選擇一張代表此地點的封面相片。只支援 JPG/JPEG，不超過 5MB。如果未選擇，系統會自動使用第一張相片作為封面。</Form.Text>
              {coverPhoto && (
                <div className="selected-photos mt-2">已選擇封面相片：{coverPhoto.name}</div>
              )}
            </Form.Group>

            <div className="d-grid gap-2">
              <Button
                variant="primary"
                type="submit"
                size="lg"
                disabled={loading}
              >
                {loading ? '正在新增...' : '新增地點'}
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => navigate(-1)}
                disabled={loading}
              >
                取消
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <Modal
        show={isMapPickerOpen}
        onHide={() => setIsMapPickerOpen(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>在地圖內選擇座標</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-2">請拖曳地圖，固定在中間的標記即為選擇位置。</p>
          <div style={{ height: '420px', borderRadius: '8px', overflow: 'hidden' }}>
            <Map
              {...mapPickerView}
              onMove={(evt) => setMapPickerView(evt.viewState)}
              onMoveEnd={(evt) => {
                const { longitude, latitude } = evt.viewState;
                setPickedCoords({ latitude, longitude });
              }}
              mapStyle="mapbox://styles/mapbox/streets-v11"
              mapboxAccessToken={config.MAPBOX_ACCESS_TOKEN}
              style={{ width: '100%', height: '100%' }}
            >
              {mapPickerView && (
                <Marker
                  longitude={mapPickerView.longitude}
                  latitude={mapPickerView.latitude}
                  color="#dc3545"
                />
              )}
            </Map>
          </div>
          <div className="mt-3">
            <strong>目前選擇：</strong>{' '}
            {pickedCoords
              ? `${pickedCoords.latitude.toFixed(6)}, ${pickedCoords.longitude.toFixed(6)}`
              : '尚未選擇'}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setIsMapPickerOpen(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={confirmMapPick}>
            使用這個座標
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AddLocationPage;
